import {
  Button,
  type Key,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  type SelectProps,
  SelectValue,
} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './InnosearchSelect.module.scss'

interface Option<T> {
  icon?: ReactNode
  label: string
  value: T
}

interface Props<T> {
  label: string
  options: Option<T>[]
  selectedItem: T | undefined
  setSelectedItem: MemoizedCallbackOrDispatch<T>
}

function InnosearchSelect<T extends Key = Key>({
  label,
  options,
  selectedItem,
  setSelectedItem,
  ...props
}: Props<T> & Omit<SelectProps<Option<T>>, 'children' | 'selectedKey' | 'onSelectionChange'>) {
  const isSupportedValues = useCallback(
    (q: unknown): q is T => {
      return options.some(option => option.value === q)
    },
    [options],
  )

  const onSelectionChange = useCallback(
    (q: unknown) => {
      if (isSupportedValues(q)) {
        // NOTE: somehow type guard is not working inside of useCallback
        setSelectedItem(q as T)
      }
    },
    [isSupportedValues, setSelectedItem],
  )

  return (
    <Select
      {...props}
      className={clsx(style.Select, props.className)}
      selectedKey={selectedItem}
      onSelectionChange={onSelectionChange}
    >
      <VisuallyHidden as={Label}>{label}</VisuallyHidden>
      <Button className={clsx(style.SelectButton, style.Light)}>
        <SelectValue className={style.Value} />
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover className={style.SelectList}>
        <ListBox className={style.ListBox}>
          {options.map(option => (
            <ListBoxItem
              className={style.ListBoxItem}
              key={option.value}
              id={option.value}
              textValue={option.label}
            >
              {option.icon}
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  )
}

export default memo(InnosearchSelect) as typeof InnosearchSelect
